import {useState} from 'react'
import {Text, Button, PopoverTrigger, PopoverContent, PopoverRoot, Portal, PopoverPositioner} from '@chakra-ui/react'
import {LuListFilter} from 'react-icons/lu'
import {useTranslate} from '@tolgee/react'
import {DatePickerSelector} from './date-picker-selector.js'
import {ListSearchInput, FilterListbox} from './list-elements.js'
import type {GenericFilter, GenericFilterOptions, OptionItem, OperatorOption, OptionIcons} from './schemas.js'
import {getCriterionIcon, getOptionIcon} from './utils.js'
export interface MultiFilterSelectorProps {
  currentFilters: GenericFilter[]
  filterOptions: GenericFilterOptions
  label: string
  onChange: (filters: GenericFilter[]) => void
  criteriaTypes: Record<string, 'regular' | 'date'>
  operators: Record<'regular' | 'date', OperatorOption[]>
  optionIcons?: OptionIcons
}

export function MultiFilterSelector({
  currentFilters,
  filterOptions,
  label,
  onChange,
  criteriaTypes,
  operators,
  optionIcons,
}: MultiFilterSelectorProps) {
  const {t} = useTranslate('common')

  if (!filterOptions) return null

  const [filter, setFilter] = useState<string>('')
  const [currentView, setCurrentView] = useState<'main' | string>('main')
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const criteriaOptions = Object.keys(criteriaTypes)
  const operatorList = operators[criteriaTypes[currentView] ?? 'regular'] ?? operators.regular

  const getFilteredCriteria = (): string[] => {
    return criteriaOptions.filter(criterion => criterion.toLowerCase().includes(filter.toLowerCase()))
  }

  const getFilteredOptions = (): OptionItem[] => {
    if (currentView === 'main' || criteriaTypes[currentView] === 'date') return []

    const options = filterOptions[currentView]
    if (!options) return []

    return options.filter(
      option =>
        option.label.toLowerCase().includes(filter.toLowerCase()) ||
        option.value.toLowerCase().includes(filter.toLowerCase())
    )
  }

  const filteredCriteria = currentView === 'main' ? getFilteredCriteria() : []
  const filteredOptions = currentView !== 'main' ? getFilteredOptions() : []

  const handleCriterionClick = (criterion: string) => {
    setCurrentView(criterion)
    setFilter('')
  }

  const handleOptionsChange = (selectedValues: string[]) => {
    if (currentView === 'main' || criteriaTypes[currentView] === 'date') return

    const existingFilterIndex = currentFilters.findIndex(f => f.id === currentView)
    let newFilters: GenericFilter[]

    if (selectedValues.length === 0) {
      // remove filter if no values selected
      if (existingFilterIndex !== -1) {
        newFilters = currentFilters.filter((_, index) => index !== existingFilterIndex)
      } else {
        return
      }
    } else if (existingFilterIndex !== -1) {
      // update or create filter
      newFilters = [...currentFilters]
      newFilters[existingFilterIndex] = {
        ...currentFilters[existingFilterIndex],
        value: selectedValues,
      }
    } else {
      newFilters = [
        ...currentFilters,
        {
          id: currentView,
          value: selectedValues,
          operator: operatorList[0].value,
        } as GenericFilter,
      ]
    }

    onChange(newFilters)
    setIsOpen(false)
    setFilter('')
    setCurrentView('main')
  }

  const dateSelectHandler = (timestamps: number[]) => {
    if (currentView === 'main') return

    let newFilters: GenericFilter[] = [
      ...currentFilters,
      {
        id: currentView,
        value: timestamps,
        operator: operatorList[0].value,
      },
    ]

    onChange(newFilters)

    setIsOpen(false)
    setFilter('')
    setCurrentView('main')
  }

  const getCurrentFilterValues = (): string[] => {
    if (currentView === 'main') return []
    const filter = currentFilters.find(f => f.id === currentView)
    return (filter?.value as string[]) || []
  }

  const hasActiveFilters = currentFilters.length > 0

  return (
    <PopoverRoot
      open={isOpen}
      onOpenChange={e => {
        setIsOpen(e.open)
        if (!e.open) {
          setFilter('')
          setCurrentView('main')
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="surface" color="gray.fg" size="sm" gap={2}>
          <LuListFilter size={4} />
          {!hasActiveFilters && <Text>{label}</Text>}
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverPositioner>
          <PopoverContent
            minWidth={criteriaTypes?.[currentView] === 'date' ? 420 : 250}
            maxWidth={criteriaTypes?.[currentView] === 'date' ? 420 : 300}
          >
            {(currentView === 'main' || criteriaTypes?.[currentView] === 'regular') && (
              <>
                <ListSearchInput
                  placeholder={currentView === 'main' ? `${t('search')}...` : `${t('search')} ${t(currentView)}...`}
                  value={filter}
                  onChange={setFilter}
                />
                {currentView === 'main' ? (
                  <FilterListbox
                    items={filteredCriteria
                      .map(criterion => ({
                        label: t(`filter.field.${criterion}`),
                        value: criterion,
                        icon: getCriterionIcon(criterion, optionIcons),
                      }))
                      .toSorted((a, b) => a.label.localeCompare(b.label))}
                    onValueChange={handleCriterionClick}
                    maxH={250}
                  />
                ) : (
                  <FilterListbox
                    items={filteredOptions.map(option => ({
                      ...option,
                      icon: getOptionIcon(option.value, currentView, optionIcons),
                    }))}
                    value={getCurrentFilterValues()}
                    multiple
                    onValueChange={handleOptionsChange}
                    maxH={250}
                  />
                )}
              </>
            )}
            {criteriaTypes?.[currentView] === 'date' && (
              <DatePickerSelector onChange={dateSelectHandler} rangeSelect={false} open={isOpen} />
            )}
          </PopoverContent>
        </PopoverPositioner>
      </Portal>
    </PopoverRoot>
  )
}
